import {FC, useEffect, useRef, useState} from 'react'
import {Button, Offcanvas, Spinner} from 'react-bootstrap'
import {BoxArrowRight, X} from 'react-bootstrap-icons'
import {useLocation, useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useAuth} from '../../hooks/useAuth'
import NavItems from './NavItems'
import {
  announceToScreenReader,
  useFocusTrap,
  useKeyboardNavigation,
  useNavigationStateStorage,
  useNavigationSwipe
} from './utils/navigationHelpers'
import {getUserDisplayName} from '../../utils/userUtils'
import {ProfileAvatar} from '../ui'
import buildInfo from '../../buildInfo.json'
import type {User} from '../../types/auth'

interface MobileNavOffcanvasProps {
  show: boolean;
  onHide: () => void;
  user: User;
}

const MobileNavOffcanvas: FC<MobileNavOffcanvasProps> = ({show, onHide, user}) => {
  const {t} = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const {logout} = useAuth()
  const offcanvasRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [prevShow, setPrevShow] = useState(show)

  if (show !== prevShow) {
    setPrevShow(show)
    if (show) {
      setIsClosing(false)
    }
  }

  const {updateLastVisited, updateMenuPreference} = useNavigationStateStorage()

  const {onTouchStart, onTouchMove, onTouchEnd, isSwiping} = useNavigationSwipe(
    () => {
      setIsClosing(true)
      setTimeout(onHide, 200)
    },
    null,
    {
      minSwipeDistance: 75,
      velocityThreshold: 0.3,
      preventVerticalSwipe: true
    }
  )

  useFocusTrap(show, offcanvasRef)

  useKeyboardNavigation({
    onEscape: () => {
      if (show) {
        announceToScreenReader('Navigation menu closed')
        onHide()
      }
    },
    enabled: show
  })

  useEffect(() => {
    if (show) {
      updateMenuPreference('lastOpenTime', Date.now())
      announceToScreenReader('Navigation menu opened')
    }
  }, [show, updateMenuPreference])

  useEffect(() => {
    if (location.pathname && show) {
      updateLastVisited(location.pathname)
    }
  }, [location.pathname, show, updateLastVisited])

  const handleLogout = async (): Promise<void> => {
    setIsLoading(true)
    announceToScreenReader('Logging out...')
    onHide()
    await logout()
    setIsLoading(false)
    navigate('/login')
  }

  const handleProfileClick = (): void => {
    announceToScreenReader('Navigating to profile')
    onHide()
    navigate('/profile')
  }

  const primaryRole = user?.roles?.[0] || 'PLAYER'
  const roleDisplay = t(`common:roles.display_names.${primaryRole}`)

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className={`offcanvas-fullscreen ${isClosing ? 'closing' : ''} ${isSwiping ? 'swiping' : ''}`}
      backdrop={true}
      scroll={false}
      ref={offcanvasRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-label="Navigation menu"
      role="navigation"
    >
      <Offcanvas.Header className="border-bottom px-3 py-3 d-flex justify-content-between align-items-center">
        <Offcanvas.Title className="fs-4 fw-semibold">
          {t('navigation.menu')}
        </Offcanvas.Title>
        <Button
          variant="link"
          className="btn-close-custom p-2 nav-close-btn ms-auto"
          onClick={onHide}
          aria-label="Close menu"
          style={{
            minHeight: '56px',
            minWidth: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease'
          }}
        >
          <X size={28} className="text-dark nav-close-icon"/>
        </Button>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0 d-flex flex-column">
        <div className="flex-grow-1 overflow-auto">
          <NavItems
            userRoles={user?.roles || []}
            user={user}
            vertical={true}
            onItemClick={onHide}
            isMobile={true}
            showIcons={true}
          />
        </div>

        <div className="mt-auto border-top">
          <button
            className="btn btn-link text-start w-100 p-4 text-decoration-none text-dark border-bottom nav-profile-btn"
            onClick={handleProfileClick}
            style={{minHeight: '80px', transition: 'background-color 0.2s ease'}}
            aria-label={`Profile: ${getUserDisplayName(user)}`}
          >
            <div className="d-flex align-items-center">
              <ProfileAvatar profileImageUrl={user?.profileImageUrl as string | undefined} userName={getUserDisplayName(user)}
                             size={40} className="me-3"/>
              <div>
                <div className="fw-semibold">
                  {getUserDisplayName(user)}
                </div>
                <small className="text-muted d-block">{user?.email}</small>
                <small className="text-muted d-block">{roleDisplay}</small>
                <small className="text-muted">v{buildInfo.version}</small>
              </div>
            </div>
          </button>

          <button
            className="btn btn-link text-start w-100 p-4 text-decoration-none text-danger nav-logout-btn"
            onClick={handleLogout}
            disabled={isLoading}
            style={{minHeight: '60px', transition: 'opacity 0.2s ease'}}
            aria-label={t('navigation.logout')}
            data-testid="logout-button"
          >
            <div className="d-flex align-items-center">
              {isLoading ? (
                <Spinner animation="border" size="sm" className="me-3"/>
              ) : (
                <BoxArrowRight size={24} className="me-3"/>
              )}
              <span
                className="fw-medium">{isLoading ? t('status.logging_out') : t('navigation.logout')}</span>
            </div>
          </button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default MobileNavOffcanvas
